import { useEffect, useState, useRef, useMemo, useContext } from "react";
import { api, fmtMoney, formatError, tError } from "@/lib/api";
import { BankLangCtx } from "@/lib/i18n";
import { toast } from "sonner";
import {
  Copy, X, Plus, Trash2, Check, Send, Building2, Users, Leaf, TrendingUp,
  HandHeart, Bitcoin, Banknote, PiggyBank, Briefcase, MessageCircle, FileText,
  HandCoins, Settings as SettingsIcon, Search, ArrowRight, CreditCard as CardIcon,
  Wallet, ChevronRight, Star, Camera, Lock, ShieldCheck, Info, Calendar, Target,
  Sparkles, Paperclip, Image as ImageIcon, Mic, Download, Play, File as FileIcon,
} from "lucide-react";

/* -------------------------------------------------------------------------- */
/* Extra CSS for pages (chat, products, modal)                                */
/* -------------------------------------------------------------------------- */
const PAGES_CSS = `
.bp-modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex; align-items: center; justify-content: center;
  z-index: 100; padding: 1rem;
  animation: bp-fade .2s ease;
}
.bp-modal {
  background: var(--surface);
  border-radius: 18px;
  max-width: 520px; width: 100%;
  max-height: 92vh; overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0,0,0,.3);
  border: 1px solid var(--line);
  animation: bp-pop .25s cubic-bezier(.2,.9,.3,1.2);
}
.bp-modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 1.1rem 1.4rem;
  border-bottom: 1px solid var(--line);
}
.bp-modal-title { font-family: 'Syne', sans-serif; font-weight: 600; font-size: 0.95rem; color: var(--navy); margin: 0; }
.bp-modal-close { width: 30px; height: 30px; border-radius: 50%; border: none; background: var(--bg); color: var(--muted); cursor: pointer; display: grid; place-items: center; }
.bp-modal-body { padding: 1.25rem 1.4rem; }
.bp-modal-footer { padding: 1rem 1.4rem; border-top: 1px solid var(--line); display: flex; gap: 8px; justify-content: flex-end; }

.bp-input, .bp-textarea, .bp-select {
  width: 100%; padding: 11px 13px;
  border-radius: 10px;
  border: 1.5px solid var(--line);
  background: var(--bg); color: var(--navy);
  font-size: .9rem; outline: none; box-sizing: border-box;
  font-family: 'DM Sans', sans-serif;
  transition: border-color .15s;
}
.bp-input:focus, .bp-textarea:focus, .bp-select:focus { border-color: var(--accent); }
.bp-label { font-size: .75rem; font-weight: 600; color: var(--muted); display: block; margin-bottom: 6px; }
.bp-field { margin-bottom: 14px; }
.bp-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }

.bp-btn {
  padding: 10px 18px; border-radius: 10px; border: none;
  font-weight: 600; font-size: .88rem; cursor: pointer;
  display: inline-flex; align-items: center; gap: 6px;
  font-family: 'DM Sans', sans-serif;
  transition: opacity .12s, transform .08s;
}
.bp-btn:active { transform: scale(.97); }
.bp-btn.primary { background: var(--accent); color: #fff; }
.bp-btn.primary:hover { opacity: .9; }
.bp-btn.ghost { background: var(--bg); color: var(--navy); border: 1px solid var(--line); }
.bp-btn.ghost:hover { background: var(--line); }
.bp-btn.danger { background: #fee2e2; color: #dc2626; }
.bp-btn.success { background: #dcfce7; color: #15803d; }

/* Product cards */
.bp-products-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}
.bp-product {
  background: var(--surface); border: 1px solid var(--line); border-radius: 16px;
  padding: 1.2rem; display: flex; flex-direction: column;
  transition: transform .15s, box-shadow .15s;
}
.bp-product:hover { transform: translateY(-3px); box-shadow: 0 10px 30px rgba(0,0,0,0.08); }
.bp-product-head { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.bp-product-icon { width: 42px; height: 42px; border-radius: 12px; display: grid; place-items: center; flex-shrink: 0; }
.bp-product-name { font-family: 'Syne', sans-serif; font-weight: 600; font-size: 0.9rem; color: var(--navy); margin: 0; line-height: 1.2; }
.bp-product-body { font-size: .82rem; color: var(--muted); line-height: 1.5; margin: 0 0 12px; flex: 1; }
.bp-product-meta {
  display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px;
}
.bp-meta-chip {
  font-size: .68rem; padding: 3px 9px; border-radius: 999px;
  background: var(--bg); border: 1px solid var(--line); color: var(--muted); font-weight: 600;
}
.bp-product-actions { display: flex; gap: 8px; margin-top: auto; }
.bp-product-actions .bp-btn { flex: 1; justify-content: center; padding: 9px; font-size: .8rem; }

/* Chat */
.bp-chat-wrap {
  display: flex; flex-direction: column;
  /* On desktop reserve room for: topbar (60), page padding (3rem) and the
     section title row (~2.5rem). 100dvh adapts to mobile browser chrome. */
  height: calc(100dvh - 60px - 1.5rem * 2 - 2.5rem);
  min-height: 360px;
  background: var(--surface); border: 1px solid var(--line); border-radius: 16px;
  overflow: hidden;
}
.bp-chat-head { padding: 14px 18px; border-bottom: 1px solid var(--line); display: flex; align-items: center; gap: 10px; }
.bp-chat-head-avatar { width: 36px; height: 36px; border-radius: 50%; background: var(--accent); color: #fff; display: grid; place-items: center; font-weight: 700; font-family: 'Syne', sans-serif; font-size: .8rem; }
.bp-chat-head-info { flex: 1; min-width: 0; }
.bp-chat-head-name { font-weight: 700; font-size: .9rem; color: var(--navy); font-family: 'Syne', sans-serif; }
.bp-chat-head-status { font-size: .7rem; color: #16a34a; display: flex; align-items: center; gap: 4px; }
.bp-chat-head-status::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: #16a34a; }
.bp-chat-body {
  flex: 1; overflow-y: auto;
  padding: 1rem 1.2rem;
  display: flex; flex-direction: column; gap: 10px;
  background: linear-gradient(to bottom, var(--bg) 0%, var(--surface) 100%);
  scrollbar-width: thin;
}
.bp-chat-empty { text-align: center; color: var(--muted); padding: 2rem 0; font-size: .85rem; }
.bp-bubble {
  max-width: 70%; padding: 9px 14px; border-radius: 16px;
  font-size: .87rem; line-height: 1.45;
  word-wrap: break-word; white-space: pre-wrap;
  box-shadow: 0 1px 2px rgba(0,0,0,.05);
}
.bp-bubble.user {
  align-self: flex-end; background: var(--accent); color: #fff;
  border-bottom-right-radius: 4px;
}
.bp-bubble.admin {
  align-self: flex-start; background: var(--surface); color: var(--navy);
  border: 1px solid var(--line); border-bottom-left-radius: 4px;
}
.bp-bubble.system {
  align-self: center; background: #fef3c7; color: #92400e;
  font-size: .76rem; max-width: 80%; text-align: center;
  border-radius: 12px;
}
.bp-bubble-time { font-size: .62rem; opacity: .65; margin-top: 4px; }

/* Attachments */
.bp-att-img { display: block; max-width: 260px; max-height: 240px; border-radius: 10px; margin-bottom: 6px; cursor: pointer; object-fit: cover; }
.bp-att-audio { display: block; max-width: 260px; margin-bottom: 6px; }
.bp-att-file { display: flex; align-items: center; gap: 10px; padding: 8px 10px; background: rgba(0,0,0,.06); border-radius: 10px; margin-bottom: 6px; text-decoration: none; color: inherit; max-width: 260px; }
.bp-bubble.user .bp-att-file { background: rgba(255,255,255,.18); }
.bp-att-file-info { flex: 1; min-width: 0; }
.bp-att-file-name { font-size: .78rem; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bp-att-file-meta { font-size: .65rem; opacity: .7; }

.bp-chat-form {
  display: flex; gap: 8px; padding: 12px 14px; align-items: center;
  border-top: 1px solid var(--line); background: var(--surface);
  position: relative;
}
.bp-attach-btn {
  width: 36px; height: 36px; border-radius: 50%; border: 1px solid var(--line);
  background: var(--bg); color: var(--muted); cursor: pointer;
  display: grid; place-items: center; transition: background .12s, color .12s;
  flex-shrink: 0;
}
.bp-attach-btn:hover { background: var(--accent); color: #fff; border-color: var(--accent); }
.bp-attach-menu {
  position: absolute; bottom: 60px; left: 14px;
  background: var(--surface); border: 1px solid var(--line); border-radius: 14px;
  box-shadow: 0 8px 24px rgba(0,0,0,.12); padding: 6px;
  display: flex; flex-direction: column; gap: 2px; min-width: 180px; z-index: 10;
}
.bp-attach-item {
  display: flex; align-items: center; gap: 10px; padding: 9px 12px;
  border-radius: 10px; border: none; background: transparent;
  font-size: .82rem; color: var(--navy); cursor: pointer; text-align: left;
  font-family: 'DM Sans', sans-serif;
}
.bp-attach-item:hover { background: var(--bg); }
.bp-attach-preview {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 10px; background: var(--bg); border: 1px solid var(--line);
  border-radius: 10px; margin: 0 14px 8px;
  font-size: .76rem; color: var(--navy);
}
.bp-attach-preview-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bp-attach-preview button { background: none; border: none; cursor: pointer; color: var(--muted); display: grid; place-items: center; }

.bp-chat-input {
  flex: 1; padding: 10px 14px; border-radius: 22px;
  border: 1px solid var(--line); background: var(--bg);
  font-family: 'DM Sans', sans-serif; font-size: .9rem; color: var(--navy);
  outline: none;
}
.bp-chat-input:focus { border-color: var(--accent); }
.bp-chat-send {
  width: 40px; height: 40px; border-radius: 50%; border: none;
  background: var(--accent); color: #fff; cursor: pointer;
  display: grid; place-items: center; transition: opacity .12s;
}
.bp-chat-send:hover { opacity: .88; }
.bp-chat-send:disabled { opacity: .4; cursor: not-allowed; }

/* IBAN display */
.bp-iban-box {
  background: linear-gradient(135deg, var(--accent) 0%, color-mix(in srgb, var(--accent), #000 20%) 100%);
  color: #fff; padding: 1.2rem 1.4rem; border-radius: 14px; margin-bottom: 14px;
  position: relative; overflow: hidden;
}
.bp-iban-box::before { content:''; position: absolute; top: -40px; right: -40px; width: 140px; height: 140px; border-radius: 50%; background: rgba(255,255,255,.1); }
.bp-iban-label { font-size: .68rem; text-transform: uppercase; letter-spacing: .13em; opacity: .8; margin-bottom: 6px; }
.bp-iban-num { font-family: 'JetBrains Mono', monospace, 'Courier New'; font-size: 1.05rem; letter-spacing: .04em; word-break: break-all; font-weight: 600; }
.bp-iban-actions { display: flex; gap: 8px; margin-top: 14px; position: relative; z-index: 1; }
.bp-iban-actions .bp-btn { background: rgba(255,255,255,.18); color: #fff; }
.bp-iban-actions .bp-btn:hover { background: rgba(255,255,255,.28); }

/* Settings */
.bp-settings-section { margin-bottom: 1.5rem; }
.bp-settings-title { font-family: 'Syne', sans-serif; font-weight: 600; font-size: .85rem; color: var(--navy); margin: 0 0 12px; display: flex; align-items: center; gap: 8px; }
.bp-avatar-upload {
  display: flex; align-items: center; gap: 16px;
  background: var(--surface); border: 1px solid var(--line); border-radius: 14px; padding: 1rem;
  margin-bottom: 1rem;
}
.bp-avatar-big {
  width: 70px; height: 70px; border-radius: 50%;
  background: var(--accent); color: #fff;
  display: grid; place-items: center; font-weight: 700;
  font-family: 'Syne', sans-serif; font-size: 1.4rem;
  overflow: hidden; position: relative;
}
.bp-avatar-big img { width: 100%; height: 100%; object-fit: cover; }

/* Savings goal card */
.bp-goal {
  background: var(--surface); border: 1px solid var(--line); border-radius: 14px;
  padding: 1rem 1.2rem; margin-bottom: 12px;
}
.bp-goal-head { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.bp-goal-icon { width: 36px; height: 36px; border-radius: 10px; display: grid; place-items: center; flex-shrink: 0; }
.bp-goal-name { font-weight: 700; font-size: .9rem; color: var(--navy); font-family: 'Syne', sans-serif; flex: 1; }
.bp-goal-pct { font-weight: 700; font-size: .85rem; color: var(--accent); }
.bp-goal-amounts { display: flex; justify-content: space-between; font-size: .78rem; margin-bottom: 6px; }
.bp-goal-actions { display: flex; gap: 6px; margin-top: 10px; }
.bp-goal-actions .bp-btn { flex: 1; padding: 7px; font-size: .78rem; justify-content: center; }

/* Requests/history list */
.bp-req {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px; background: var(--surface); border: 1px solid var(--line);
  border-radius: 12px; margin-bottom: 8px;
}
.bp-req-info { flex: 1; min-width: 0; }
.bp-req-name { font-weight: 600; font-size: .87rem; color: var(--navy); }
.bp-req-sub { font-size: .73rem; color: var(--muted); margin-top: 2px; }
.bp-req-amount { font-weight: 700; font-size: .9rem; color: var(--navy); }
.bp-status { font-size: .68rem; padding: 3px 9px; border-radius: 999px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; }
.bp-status.pending { background: #fef3c7; color: #b45309; }
.bp-status.approved { background: #dcfce7; color: #15803d; }
.bp-status.rejected { background: #fee2e2; color: #b91c1c; }
.bp-status.active { background: #dbeafe; color: #1d4ed8; }
.bp-status.withdrawn { background: #ede9fe; color: #6d28d9; }

@keyframes bp-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes bp-pop { from { opacity: 0; transform: scale(.94); } to { opacity: 1; transform: scale(1); } }

/* Mobile */
@media (max-width: 767px) {
  .bp-products-grid { grid-template-columns: 1fr; }
  .bp-row { grid-template-columns: 1fr; }
  /* Messages page must show input + last messages WITHOUT scrolling the page:
       topbar (56) + bottom-nav (76) + page-inner padding top/bottom (32)
       + section title row .db-page-h with its margin (~50). 100dvh adapts
       to the iOS/Android address-bar collapse animation. */
  .bp-chat-wrap {
    height: calc(100dvh - 56px - 76px - 32px - 50px);
    min-height: 300px;
  }
  /* Tighten the message-page wrapper so the chat block uses every spare pixel */
  .db-page-inner.has-chat { padding: 0.75rem 0.75rem 0.25rem; }
  .db-page-inner.has-chat .db-page-h { margin-bottom: 0.6rem; font-size: 1rem; }
  .bp-iban-num { font-size: .85rem; }
}
`;

/* -------------------------------------------------------------------------- */
/* Generic Modal                                                              */
/* -------------------------------------------------------------------------- */
export function Modal({ open, onClose, title, children, footer }) {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="bp-modal-backdrop" onClick={onClose}>
      <style>{PAGES_CSS}</style>
      <div className="bp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bp-modal-header">
          <h3 className="bp-modal-title">{title}</h3>
          <button className="bp-modal-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        <div className="bp-modal-body">{children}</div>
        {footer && <div className="bp-modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* IBAN MODAL                                                                  */
/* -------------------------------------------------------------------------- */
export function IbanModal({ open, onClose, headers, bank }) {
  const { t } = useContext(BankLangCtx);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !headers) return;
    api.get("/bank-user/iban", { headers })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(tError(e, t)));
  }, [open, headers]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text.replace(/\s/g, ""));
      toast.success(t("iban.copied"));
    } catch { toast.error(t("iban.copy_fail")); }
  };

  return (
    <Modal open={open} onClose={onClose} title={t("iban.title")}>
      {!data ? (
        <div style={{ textAlign: "center", padding: "30px 0", color: "var(--muted)", fontSize: ".85rem" }}>{t("iban.loading")}</div>
      ) : (
        <>
          <div className="bp-iban-box">
            <div className="bp-iban-label">IBAN — {bank.name}</div>
            <div className="bp-iban-num" data-testid="user-iban">{data.iban}</div>
            <div className="bp-iban-actions">
              <button className="bp-btn" onClick={() => copy(data.iban)}><Copy size={14} />{t("iban.copy")}</button>
            </div>
          </div>
          <div className="bp-field">
            <div className="bp-label">{t("iban.bic")}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <code style={{ flex: 1, padding: "10px 13px", background: "var(--bg)", borderRadius: 10, border: "1px solid var(--line)", fontSize: ".88rem", fontFamily: "monospace", color: "var(--navy)" }}>{data.bic}</code>
              <button className="bp-btn ghost" onClick={() => copy(data.bic)}><Copy size={14} /></button>
            </div>
          </div>
          <div style={{ background: "var(--bg)", padding: 12, borderRadius: 10, border: "1px solid var(--line)", fontSize: ".78rem", color: "var(--muted)", lineHeight: 1.5 }}>
            <Info size={14} style={{ display: "inline", marginRight: 6, verticalAlign: "middle" }} />
            {t("iban.share_info")}
          </div>
        </>
      )}
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* ACCOUNTS PAGE                                                              */
/* -------------------------------------------------------------------------- */
export function AccountsPage({ bank, style, headers, me }) {
  const { t } = useContext(BankLangCtx);
  const [accounts, setAccounts] = useState(null);

  useEffect(() => {
    if (!headers) return;
    api.get("/bank-user/accounts", { headers }).then((r) => setAccounts(r.data)).catch(() => setAccounts([]));
  }, [headers]);

  const copy = async (txt) => {
    try { await navigator.clipboard.writeText(txt.replace(/\s/g, "")); toast.success(t("accounts.iban_copied")); }
    catch { toast.error(t("accounts.copy_fail")); }
  };

  if (!accounts) return <div style={{ padding: "20px 0", textAlign: "center", color: "var(--muted)" }}>{t("accounts.loading")}</div>;

  return (
    <>
      <style>{PAGES_CSS}</style>
      <p style={{ color: "var(--muted)", fontSize: ".85rem", margin: "0 0 1.25rem" }}>{t("accounts.all", { bank: bank.name })}</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {accounts.map((acc) => (
          <div key={acc.id} style={{ background: acc.type === "main" ? style.cardGradient : "var(--surface)", color: acc.type === "main" ? "#fff" : "var(--navy)", border: acc.type === "main" ? "none" : "1px solid var(--line)", borderRadius: 16, padding: "1.2rem 1.4rem", position: "relative", overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: ".68rem", textTransform: "uppercase", letterSpacing: ".12em", opacity: .75, fontWeight: 600 }}>{acc.type === "main" ? t("accounts.primary") : t("accounts.savings")}</div>
                <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: "1rem", marginTop: 4 }}>{acc.name}</div>
              </div>
              <span className={`bp-status active`} style={acc.type === "main" ? { background: "rgba(255,255,255,.22)", color: "#fff" } : {}}>{acc.type === "main" ? t("accounts.primary_tag") : t("accounts.secondary_tag")}</span>
            </div>
            {(() => {
              const s = fmtMoney(acc.balance, me?.currency || bank?.default_currency);
              const fs = Math.min(1.8, Math.max(0.72, 19.8 / s.length));
              return <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: `${fs}rem`, letterSpacing: "-.03em", fontVariantNumeric: "tabular-nums lining-nums", marginTop: 14 }}>{s}</div>;
            })()}
            {acc.type === "savings" && acc.target > 0 && (
              <div style={{ marginTop: 10, fontSize: ".75rem", color: "var(--muted)" }}>
                {t("accounts.objective", { amount: fmtMoney(acc.target, me?.currency || bank?.default_currency), pct: Math.round((acc.balance / acc.target) * 100) })}
              </div>
            )}
            <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <code style={{ flex: 1, minWidth: 200, padding: "9px 13px", background: acc.type === "main" ? "rgba(0,0,0,.18)" : "var(--bg)", borderRadius: 10, fontSize: ".82rem", fontFamily: "monospace", color: acc.type === "main" ? "#fff" : "var(--navy)", border: acc.type === "main" ? "none" : "1px solid var(--line)" }}>{acc.iban}</code>
              <button onClick={() => copy(acc.iban)} className="bp-btn" style={acc.type === "main" ? { background: "rgba(255,255,255,.2)", color: "#fff" } : { background: "var(--bg)", color: "var(--navy)", border: "1px solid var(--line)" }}>
                <Copy size={13} />{t("accounts.copy")}
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* SAVINGS PAGE                                                                */
/* -------------------------------------------------------------------------- */
export function SavingsPage({ bank, style, headers, me, reload }) {
  const { t } = useContext(BankLangCtx);
  const [goals, setGoals] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ name: "", target_amount: "", deadline: "", initial_deposit: "", color: "#10b981" });
  const [depositGoal, setDepositGoal] = useState(null);
  const [depositAmount, setDepositAmount] = useState("");
  const [withdrawGoal, setWithdrawGoal] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [removeGoal, setRemoveGoal] = useState(null);

  const fetchGoals = async () => {
    try { const { data } = await api.get("/bank-user/savings", { headers }); setGoals(data); }
    catch (e) { setGoals([]); }
  };
  useEffect(() => { if (headers) fetchGoals(); }, [headers]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/bank-user/savings", {
        name: form.name,
        target_amount: Number(form.target_amount),
        deadline: form.deadline || null,
        initial_deposit: Number(form.initial_deposit || 0),
        color: form.color,
      }, { headers });
      toast.success(t("savings.toast.created"));
      setModalOpen(false);
      setForm({ name: "", target_amount: "", deadline: "", initial_deposit: "", color: "#10b981" });
      fetchGoals(); reload();
    } catch (err) { toast.error(tError(err, t)); }
  };

  const deposit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/bank-user/savings/${depositGoal.id}/deposit`, { amount: Number(depositAmount) }, { headers });
      toast.success(t("savings.toast.deposited"));
      setDepositGoal(null); setDepositAmount("");
      fetchGoals(); reload();
    } catch (err) { toast.error(tError(err, t)); }
  };

  const withdraw = (goal) => { setWithdrawGoal(goal); setWithdrawAmount(""); };

  const doWithdraw = async (e) => {
    e?.preventDefault();
    if (!withdrawAmount) return;
    try {
      await api.post(`/bank-user/savings/${withdrawGoal.id}/withdraw`, { amount: Number(withdrawAmount) }, { headers });
      toast.success(t("savings.toast.withdrawn"));
      setWithdrawGoal(null); setWithdrawAmount("");
      fetchGoals(); reload();
    } catch (err) { toast.error(tError(err, t)); }
  };

  const remove = (goal) => { setRemoveGoal(goal); };

  const doRemove = async () => {
    try {
      await api.delete(`/bank-user/savings/${removeGoal.id}`, { headers });
      toast.success(t("savings.toast.closed"));
      setRemoveGoal(null); fetchGoals(); reload();
    } catch (err) { toast.error(tError(err, t)); }
  };

  if (!goals) return <div style={{ padding: "20px 0", textAlign: "center", color: "var(--muted)" }}>{t("savings.loading")}</div>;

  const totalSaved = goals.reduce((s, g) => s + Number(g.saved_amount || 0), 0);
  const totalTarget = goals.reduce((s, g) => s + Number(g.target_amount || 0), 0);
  const overallPct = totalTarget > 0 ? Math.round((totalSaved / totalTarget) * 100) : 0;

  return (
    <>
      <style>{PAGES_CSS}</style>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 1.25 + "rem" }}>
        <div className="db-widget" style={{ padding: "1rem 1.2rem" }}>
          <div style={{ fontSize: ".72rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".1em", fontWeight: 600 }}>{t("savings.total")}</div>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: "1.3rem", color: "var(--navy)", marginTop: 4, letterSpacing: "-.03em", fontVariantNumeric: "tabular-nums lining-nums" }}>{fmtMoney(totalSaved, me?.currency || bank?.default_currency)}</div>
        </div>
        <div className="db-widget" style={{ padding: "1rem 1.2rem" }}>
          <div style={{ fontSize: ".72rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".1em", fontWeight: 600 }}>{t("savings.target_total")}</div>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: "1.3rem", color: "var(--navy)", marginTop: 4, letterSpacing: "-.03em", fontVariantNumeric: "tabular-nums lining-nums" }}>{fmtMoney(totalTarget, me?.currency || bank?.default_currency)}</div>
        </div>
        <div className="db-widget" style={{ padding: "1rem 1.2rem" }}>
          <div style={{ fontSize: ".72rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".1em", fontWeight: 600 }}>{t("savings.progress")}</div>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: "1.3rem", color: style.accent, marginTop: 4, letterSpacing: "-.02em" }}>{overallPct}%</div>
        </div>
        <div className="db-widget" style={{ padding: "1rem 1.2rem" }}>
          <div style={{ fontSize: ".72rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".1em", fontWeight: 600 }}>{t("savings.active")}</div>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: "1.3rem", color: "var(--navy)", marginTop: 4 }}>{goals.length}</div>
        </div>
      </div>

      <button className="bp-btn primary" onClick={() => setModalOpen(true)} style={{ marginBottom: 16 }} data-testid="savings-new">
        <Plus size={15} />{t("savings.new_goal")}
      </button>

      {goals.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 20px", background: "var(--surface)", border: "1px dashed var(--line)", borderRadius: 14 }}>
          <PiggyBank size={36} style={{ opacity: .4, marginBottom: 10 }} />
          <p style={{ color: "var(--muted)", fontSize: ".88rem", margin: 0 }}>{t("savings.none")}<br />{t("savings.none_sub")}</p>
        </div>
      ) : (
        <div>
          {goals.map((g) => {
            const pct = g.target_amount > 0 ? Math.min(100, Math.round((g.saved_amount / g.target_amount) * 100)) : 0;
            return (
              <div key={g.id} className="bp-goal">
                <div className="bp-goal-head">
                  <div className="bp-goal-icon" style={{ background: (g.color || "#10b981") + "22" }}>
                    <Target size={17} color={g.color || "#10b981"} />
                  </div>
                  <span className="bp-goal-name">{g.name}</span>
                  <span className="bp-goal-pct">{pct}%</span>
                </div>
                <div className="bp-goal-amounts">
                  <span style={{ color: "var(--muted)" }}>{t("savings.saved")} <strong style={{ color: "var(--navy)" }}>{fmtMoney(g.saved_amount, me?.currency || bank?.default_currency)}</strong></span>
                  <span style={{ color: "var(--muted)" }}>{t("savings.goal")} <strong style={{ color: "var(--navy)" }}>{fmtMoney(g.target_amount, me?.currency || bank?.default_currency)}</strong></span>
                </div>
                <div style={{ height: 8, borderRadius: 999, background: "var(--line)", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${pct}%`, background: g.color || "#10b981", borderRadius: 999, transition: "width .5s" }} />
                </div>
                {g.deadline && (
                  <div style={{ fontSize: ".72rem", color: "var(--muted)", marginTop: 8 }}>
                    <Calendar size={11} style={{ display: "inline", marginRight: 4, verticalAlign: "middle" }} />
                    {t("savings.deadline", { date: new Date(g.deadline).toLocaleDateString() })}
                  </div>
                )}
                <div className="bp-goal-actions">
                  <button className="bp-btn primary" onClick={() => { setDepositGoal(g); setDepositAmount(""); }}>{t("savings.deposit")}</button>
                  <button className="bp-btn ghost" onClick={() => withdraw(g)}>{t("savings.withdraw")}</button>
                  <button className="bp-btn danger" onClick={() => remove(g)} style={{ flex: "0 0 auto", padding: "7px 12px" }}><Trash2 size={13} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={t("savings.form.title")} footer={(
        <>
          <button className="bp-btn ghost" onClick={() => setModalOpen(false)}>{t("savings.form.cancel")}</button>
          <button className="bp-btn primary" onClick={create} data-testid="savings-submit">{t("savings.form.create")}</button>
        </>
      )}>
        <form onSubmit={create}>
          <div className="bp-field">
            <label className="bp-label">{t("savings.form.name")}</label>
            <input className="bp-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("savings.form.name_ph")} />
          </div>
          <div className="bp-row">
            <div className="bp-field">
              <label className="bp-label">{t("savings.form.target")}</label>
              <input className="bp-input" type="number" min="1" required value={form.target_amount} onChange={(e) => setForm({ ...form, target_amount: e.target.value })} placeholder="100000" />
            </div>
            <div className="bp-field">
              <label className="bp-label">{t("savings.form.initial")}</label>
              <input className="bp-input" type="number" min="0" value={form.initial_deposit} onChange={(e) => setForm({ ...form, initial_deposit: e.target.value })} placeholder="0" />
            </div>
          </div>
          <div className="bp-row">
            <div className="bp-field">
              <label className="bp-label">{t("savings.form.deadline")}</label>
              <input className="bp-input" type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
            </div>
            <div className="bp-field">
              <label className="bp-label">{t("savings.form.color")}</label>
              <input className="bp-input" type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} style={{ height: 42, padding: 4 }} />
            </div>
          </div>
        </form>
      </Modal>

      {/* Deposit modal */}
      <Modal open={!!depositGoal} onClose={() => setDepositGoal(null)} title={t("savings.deposit_title", { name: depositGoal?.name || "" })} footer={(
        <>
          <button className="bp-btn ghost" onClick={() => setDepositGoal(null)}>{t("savings.form.cancel")}</button>
          <button className="bp-btn primary" onClick={deposit}>{t("savings.deposit")}</button>
        </>
      )}>
        <form onSubmit={deposit}>
          <div className="bp-field">
            <label className="bp-label">{t("savings.deposit_amount")}</label>
            <input className="bp-input" type="number" min="1" required value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} placeholder="0" autoFocus />
            <div style={{ fontSize: ".72rem", color: "var(--muted)", marginTop: 6 }}>{t("savings.balance_hint", { amount: fmtMoney(me?.balance || 0, me?.currency || bank?.default_currency) })}</div>
          </div>
        </form>
      </Modal>

      {/* Withdraw modal */}
      <Modal open={!!withdrawGoal} onClose={() => setWithdrawGoal(null)} title={t("savings.withdraw_prompt", { name: withdrawGoal?.name || "" })} footer={(
        <>
          <button className="bp-btn ghost" onClick={() => setWithdrawGoal(null)}>{t("savings.form.cancel")}</button>
          <button className="bp-btn primary" onClick={doWithdraw}>{t("savings.withdraw")}</button>
        </>
      )}>
        <form onSubmit={doWithdraw}>
          <div className="bp-field">
            <label className="bp-label">{t("savings.deposit_amount")}</label>
            <input className="bp-input" type="number" min="1" required value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)} placeholder="0" autoFocus />
            <div style={{ fontSize: ".72rem", color: "var(--muted)", marginTop: 6 }}>{t("savings.saved")} <strong>{fmtMoney(withdrawGoal?.saved_amount || 0, me?.currency || bank?.default_currency)}</strong></div>
          </div>
        </form>
      </Modal>

      {/* Remove confirmation modal */}
      <Modal open={!!removeGoal} onClose={() => setRemoveGoal(null)} title={t("savings.close_confirm", { name: removeGoal?.name || "" })} footer={(
        <>
          <button className="bp-btn ghost" onClick={() => setRemoveGoal(null)}>{t("savings.form.cancel")}</button>
          <button className="bp-btn danger" onClick={doRemove}>{t("common.confirm")}</button>
        </>
      )}>
        <p style={{ color: "var(--muted)", fontSize: ".88rem", margin: 0, lineHeight: 1.6 }}>{t("savings.close_confirm", { name: removeGoal?.name || "" })}</p>
      </Modal>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* INVESTMENT PRODUCTS                                                         */
/* -------------------------------------------------------------------------- */
const INVESTMENT_PRODUCTS_DEFS = [
  { id: "immobilier", nameKey: "invest.p1.name", Icon: Building2, color: "#0ea5e9", bodyKey: "invest.p1.desc" },
  { id: "crowdfunding", nameKey: "invest.p2.name", Icon: Users, color: "#f59e0b", bodyKey: "invest.p2.desc" },
  { id: "energies", nameKey: "invest.p3.name", Icon: Leaf, color: "#10b981", bodyKey: "invest.p3.desc" },
  { id: "bourse", nameKey: "invest.p4.name", Icon: TrendingUp, color: "#8b5cf6", bodyKey: "invest.p4.desc" },
  { id: "participatif", nameKey: "invest.p5.name", Icon: HandHeart, color: "#ec4899", bodyKey: "invest.p5.desc" },
  { id: "crypto", nameKey: "invest.p6.name", Icon: Bitcoin, color: "#f97316", bodyKey: "invest.p6.desc" },
];

export function InvestmentPage({ bank, style, headers, me, setActiveSection }) {
  const { t } = useContext(BankLangCtx);
  const [products, setProducts] = useState([]);
  const INVESTMENT_PRODUCTS = products;
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(null);
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [withdrawTarget, setWithdrawTarget] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/bank-user/investments", { headers }); setHistory(data); }
    catch { setHistory([]); }
  };
  const loadProducts = async () => {
    try {
      const { data } = await api.get("/bank-user/investment-products", { headers });
      setProducts(data.map((p) => ({ id: p.id, name: p.name, body: p.description || "", color: p.color || "#0ea5e9", Icon: Briefcase })));
    } catch {
      setProducts(INVESTMENT_PRODUCTS_DEFS.map((p) => ({ ...p, name: t(p.nameKey), body: t(p.bodyKey) })));
    }
  };
  useEffect(() => { if (headers) { load(); loadProducts(); } }, [headers]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/bank-user/investments", {
        product_id: selected.id,
        product_name: selected.name,
        amount: Number(amount),
        notes,
      }, { headers });
      toast.success(t("invest.toast.requested"));
      setSelected(null); setAmount(""); setNotes("");
      load();
      setActiveSection("messages");
    } catch (err) { toast.error(tError(err, t)); }
  };

  const confirmWithdraw = async () => {
    if (!withdrawTarget) return;
    setWithdrawing(true);
    try {
      const { data } = await api.post(
        `/bank-user/investments/${withdrawTarget.id}/withdraw`,
        {},
        { headers },
      );
      toast.success(t("invest.withdraw.success", { amount: fmtMoney(data.total, me?.currency || bank?.default_currency) }));
      setWithdrawTarget(null);
      load();
      // Tell parent to reload user (for balance refresh)
      window.dispatchEvent(new CustomEvent("bank-user-reload"));
    } catch (err) { toast.error(tError(err, t)); }
    finally { setWithdrawing(false); }
  };

  const totalInvested = history.filter((h) => h.status === "approved").reduce((s, h) => s + Number(h.amount), 0);
  const totalProfit = history.filter((h) => h.status === "approved" || h.status === "withdrawn").reduce((s, h) => s + Number(h.profit || 0), 0);

  return (
    <>
      <style>{PAGES_CSS}</style>
      <p style={{ color: "var(--muted)", fontSize: ".85rem", margin: "0 0 1.25rem" }}>{t("invest.cta_sub")}</p>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: "1.25rem" }}>
        <div className="db-widget" style={{ padding: "1rem 1.2rem" }}>
          <div style={{ fontSize: ".72rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".1em", fontWeight: 600 }}>{t("invest.total")}</div>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: "1.3rem", color: "var(--navy)", marginTop: 4, letterSpacing: "-.03em", fontVariantNumeric: "tabular-nums lining-nums" }}>{fmtMoney(totalInvested, me?.currency || bank?.default_currency)}</div>
        </div>
        <div className="db-widget" style={{ padding: "1rem 1.2rem" }}>
          <div style={{ fontSize: ".72rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".1em", fontWeight: 600 }}>{t("invest.profits")}</div>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: "1.3rem", color: "#16a34a", marginTop: 4, letterSpacing: "-.03em", fontVariantNumeric: "tabular-nums lining-nums" }}>+{fmtMoney(totalProfit, me?.currency || bank?.default_currency)}</div>
        </div>
        <div className="db-widget" style={{ padding: "1rem 1.2rem" }}>
          <div style={{ fontSize: ".72rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".1em", fontWeight: 600 }}>{t("invest.count")}</div>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: "1.3rem", color: "var(--navy)", marginTop: 4 }}>{history.length}</div>
        </div>
      </div>

      {/* Products */}
      <h3 style={{ fontFamily: "'Syne', sans-serif", fontSize: ".95rem", fontWeight: 700, color: "var(--navy)", margin: "0 0 14px" }}>{t("invest.products")}</h3>
      <div className="bp-products-grid">
        {INVESTMENT_PRODUCTS.map((p) => (
          <div key={p.id} className="bp-product">
            <div className="bp-product-head">
              <div className="bp-product-icon" style={{ background: p.color + "1F" }}>
                <p.Icon size={20} color={p.color} />
              </div>
              <h4 className="bp-product-name">{p.name}</h4>
            </div>
            <p className="bp-product-body">{p.body}</p>
            <div className="bp-product-actions">
              <button className="bp-btn primary" onClick={() => { setSelected(p); setAmount(""); setNotes(""); }} data-testid={`invest-${p.id}`}>{t("invest.invest_btn")}</button>
            </div>
          </div>
        ))}
      </div>

      {/* History */}
      {history.length > 0 && (
        <>
          <h3 style={{ fontFamily: "'Syne', sans-serif", fontSize: ".95rem", fontWeight: 700, color: "var(--navy)", margin: "1.5rem 0 14px" }}>{t("invest.my")}</h3>
          <div>
            {history.map((h) => {
              const canWithdraw = h.status === "approved" && !h.withdrawn_at;
              const isWithdrawn = h.status === "withdrawn" || !!h.withdrawn_at;
              const statusLabel = isWithdrawn
                ? t("invest.withdrawn")
                : h.status === "pending"
                  ? t("invest.pending")
                  : h.status === "approved"
                    ? t("invest.validated")
                    : t("invest.refused");
              return (
                <div key={h.id} className="bp-req">
                  <div className="bp-product-icon" style={{ background: style.accent + "18", width: 36, height: 36 }}>
                    <Briefcase size={16} color={style.accent} />
                  </div>
                  <div className="bp-req-info">
                    <div className="bp-req-name">{h.product_name}</div>
                    <div className="bp-req-sub">{new Date(h.created_at).toLocaleDateString()}{h.profit > 0 ? ` · +${fmtMoney(h.profit, me?.currency || bank?.default_currency)}` : ""}</div>
                  </div>
                  <div className="bp-req-amount">{fmtMoney(h.amount, me?.currency || bank?.default_currency)}</div>
                  <span className={`bp-status ${isWithdrawn ? "withdrawn" : h.status}`}>{statusLabel}</span>
                  {canWithdraw && (
                    <button
                      type="button"
                      className="bp-btn primary"
                      style={{ marginLeft: 10, padding: "6px 12px", fontSize: ".78rem" }}
                      onClick={() => setWithdrawTarget(h)}
                      data-testid={`invest-withdraw-${h.id}`}
                    >
                      <Wallet size={13} style={{ marginRight: 4, verticalAlign: "middle" }} />
                      {t("invest.withdraw.btn")}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Invest modal */}
      <Modal open={!!selected} onClose={() => setSelected(null)} title={selected ? `${t("invest.invest_btn")} — ${selected.name}` : ""} footer={(
        <>
          <button className="bp-btn ghost" onClick={() => setSelected(null)}>{t("invest.form.cancel")}</button>
          <button className="bp-btn primary" onClick={submit}>{t("invest.form.submit")}</button>
        </>
      )}>
        {selected && (
          <form onSubmit={submit}>
            <div style={{ background: selected.color + "12", border: `1px solid ${selected.color}40`, borderRadius: 12, padding: 14, marginBottom: 14, fontSize: ".82rem", color: "var(--navy)", lineHeight: 1.5 }}>
              <selected.Icon size={18} color={selected.color} style={{ verticalAlign: "middle", marginRight: 8 }} />
              {selected.body}
            </div>
            <div className="bp-field">
              <label className="bp-label">{t("invest.form.amount")}</label>
              <input className="bp-input" type="number" min="1" required value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" autoFocus />
              <div style={{ fontSize: ".72rem", color: "var(--muted)", marginTop: 6 }}>{t("invest.form.balance", { amount: fmtMoney(me.balance, me?.currency || bank?.default_currency) })}</div>
            </div>
            <div className="bp-field">
              <label className="bp-label">{t("invest.form.note")}</label>
              <textarea className="bp-textarea" rows="3" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("invest.form.note_ph")} />
            </div>
            <div style={{ fontSize: ".75rem", color: "var(--muted)", background: "var(--bg)", padding: 10, borderRadius: 8, border: "1px solid var(--line)" }}>
              <Info size={12} style={{ verticalAlign: "middle", marginRight: 5 }} />
              {t("invest.form.advisor_info")}
            </div>
          </form>
        )}
      </Modal>

      {/* Withdraw confirmation modal */}
      <Modal
        open={!!withdrawTarget}
        onClose={() => !withdrawing && setWithdrawTarget(null)}
        title={t("invest.withdraw.title")}
        footer={(
          <>
            <button className="bp-btn ghost" disabled={withdrawing} onClick={() => setWithdrawTarget(null)}>{t("invest.form.cancel")}</button>
            <button className="bp-btn primary" disabled={withdrawing} onClick={confirmWithdraw} data-testid="invest-withdraw-confirm">
              {withdrawing ? "…" : t("invest.withdraw.confirm")}
            </button>
          </>
        )}
      >
        {withdrawTarget && (() => {
          const principal = Number(withdrawTarget.amount) || 0;
          const profit = Number(withdrawTarget.profit) || 0;
          const total = principal + profit;
          const cur = me?.currency || bank?.default_currency;
          return (
            <div>
              <p style={{ color: "var(--muted)", fontSize: ".88rem", lineHeight: 1.55, margin: "0 0 16px" }}>
                {t("invest.withdraw.intro", { product: withdrawTarget.product_name || "" })}
              </p>
              <div style={{ background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 12, padding: "14px 16px", marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: ".88rem" }}>
                  <span style={{ color: "var(--muted)" }}>{t("invest.withdraw.principal")}</span>
                  <strong style={{ color: "var(--navy)", fontVariantNumeric: "tabular-nums lining-nums" }}>{fmtMoney(principal, cur)}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px dashed var(--line)", fontSize: ".88rem" }}>
                  <span style={{ color: "var(--muted)" }}>{t("invest.withdraw.profit")}</span>
                  <strong style={{ color: "#16a34a", fontVariantNumeric: "tabular-nums lining-nums" }}>+{fmtMoney(profit, cur)}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 4px", fontSize: ".95rem" }}>
                  <span style={{ color: "var(--navy)", fontWeight: 700 }}>{t("invest.withdraw.total")}</span>
                  <strong style={{ color: style.accent, fontSize: "1.05rem", fontVariantNumeric: "tabular-nums lining-nums" }}>{fmtMoney(total, cur)}</strong>
                </div>
              </div>
              <div style={{ fontSize: ".78rem", color: "var(--muted)", background: "#fef3c7", border: "1px solid #fcd34d", padding: "10px 12px", borderRadius: 10, lineHeight: 1.5 }}>
                <Info size={12} style={{ verticalAlign: "middle", marginRight: 5 }} />
                {t("invest.withdraw.warning")}
              </div>
            </div>
          );
        })()}
      </Modal>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* MESSAGES PAGE                                                              */
/* -------------------------------------------------------------------------- */
const MAX_ATTACH_BYTES = 8 * 1024 * 1024; // 8 MB

function detectKind(mime, name = "") {
  const m = (mime || "").toLowerCase();
  const n = (name || "").toLowerCase();
  if (m.startsWith("image/")) return "image";
  if (m.startsWith("audio/")) return "audio";
  if (m === "application/pdf" || n.endsWith(".pdf")) return "pdf";
  return "file";
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export function MessageBubble({ m }) {
  const { t } = useContext(BankLangCtx);
  const att = m.attachment_url;
  const kind = m.attachment_kind || (att ? detectKind(m.attachment_type, m.attachment_name) : null);
  return (
    <div className={`bp-bubble ${m.from_role}`}>
      {att && kind === "image" && (
        <img src={att} alt={m.attachment_name || "image"} className="bp-att-img" onClick={() => window.open(att, "_blank")} />
      )}
      {att && kind === "audio" && (
        <audio controls src={att} className="bp-att-audio" />
      )}
      {att && (kind === "pdf" || kind === "file") && (
        <a className="bp-att-file" href={att} download={m.attachment_name || t("msg.file")} target="_blank" rel="noreferrer">
          {kind === "pdf" ? <FileText size={22} /> : <FileIcon size={22} />}
          <div className="bp-att-file-info">
            <div className="bp-att-file-name">{m.attachment_name || (kind === "pdf" ? t("msg.pdf_doc") : t("msg.file"))}</div>
            <div className="bp-att-file-meta">{kind === "pdf" ? "PDF" : (m.attachment_type || t("msg.file"))}</div>
          </div>
          <Download size={16} style={{ opacity: .7 }} />
        </a>
      )}
      {m.text && <div>{m.text}</div>}
      <div className="bp-bubble-time" style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: m.from_role === "user" ? "flex-end" : "flex-start" }}>
        {new Date(m.created_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
        {m.from_role === "user" && (
          <span style={{ display: "inline-flex", alignItems: "center", opacity: m.read ? 1 : 0.55 }} title={m.read ? t("msg.read") : t("msg.sent")}>
            {m.read ? (
              <svg width="16" height="10" viewBox="0 0 16 10" fill="none">
                <path d="M1 5l3 3 6-7" stroke="#93c5fd" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5 5l3 3 6-7" stroke="#93c5fd" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M1 5l3 3 5-6" stroke="rgba(255,255,255,0.6)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

export function MessagesPage({ bank, style, headers, me }) {
  const { t } = useContext(BankLangCtx);
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState(null); // {file, dataUrl, kind}
  const [menuOpen, setMenuOpen] = useState(false);
  const bodyRef = useRef(null);
  const imgInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchMsgs = async () => {
    try { const { data } = await api.get("/bank-user/messages", { headers }); setMsgs(data); }
    catch { setMsgs([]); }
  };

  useEffect(() => {
    if (!headers) return;
    fetchMsgs();
    const interval = setInterval(fetchMsgs, 4000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [msgs]);

  const pickFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_ATTACH_BYTES) {
      toast.error(t("msg.too_large", { size: fmtSize(MAX_ATTACH_BYTES) }));
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    setPending({ file, dataUrl, kind: detectKind(file.type, file.name) });
    setMenuOpen(false);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !pending) return;
    setSending(true);
    try {
      const payload = { text: text.trim() };
      if (pending) {
        payload.attachment_url = pending.dataUrl;
        payload.attachment_name = pending.file.name;
        payload.attachment_type = pending.file.type || "application/octet-stream";
        payload.attachment_kind = pending.kind;
      }
      await api.post("/bank-user/messages", payload, { headers });
      setText("");
      setPending(null);
      fetchMsgs();
    } catch (err) { toast.error(tError(err, t)); }
    finally { setSending(false); }
  };

  const initials = (bank.name || "?")[0].toUpperCase();

  return (
    <>
      <style>{PAGES_CSS}</style>
      <div className="bp-chat-wrap" data-testid="messages-chat">
        <div className="bp-chat-head">
          <div className="bp-chat-head-avatar">{initials}</div>
          <div className="bp-chat-head-info">
            <div className="bp-chat-head-name">{bank.name}</div>
            <div className="bp-chat-head-status">{t("msg.online")}</div>
          </div>
        </div>
        <div className="bp-chat-body" ref={bodyRef}>
          {!msgs ? (
            <div className="bp-chat-empty">{t("msg.loading")}</div>
          ) : msgs.length === 0 ? (
            <div className="bp-chat-empty">
              <MessageCircle size={36} style={{ opacity: .35, marginBottom: 10 }} /><br />
              {t("msg.none")}
            </div>
          ) : (
            msgs.map((m) => <MessageBubble key={m.id} m={m} />)
          )}
        </div>

        {pending && (
          <div className="bp-attach-preview" data-testid="messages-attach-preview">
            {pending.kind === "image" ? <ImageIcon size={16} /> : pending.kind === "audio" ? <Mic size={16} /> : pending.kind === "pdf" ? <FileText size={16} /> : <FileIcon size={16} />}
            <span className="bp-attach-preview-name">{pending.file.name} <span style={{ opacity: .6 }}>({fmtSize(pending.file.size)})</span></span>
            <button type="button" onClick={() => setPending(null)} aria-label={t("msg.withdraw")}><X size={14} /></button>
          </div>
        )}

        <form className="bp-chat-form" onSubmit={send}>
          {menuOpen && (
            <div className="bp-attach-menu" onMouseLeave={() => setMenuOpen(false)}>
              <button type="button" className="bp-attach-item" onClick={() => imgInputRef.current?.click()}><ImageIcon size={16} color="#22c55e" />{t("msg.attach.image")}</button>
              <button type="button" className="bp-attach-item" onClick={() => pdfInputRef.current?.click()}><FileText size={16} color="#ef4444" />{t("msg.attach.pdf")}</button>
              <button type="button" className="bp-attach-item" onClick={() => audioInputRef.current?.click()}><Mic size={16} color="#8b5cf6" />{t("msg.attach.audio")}</button>
              <button type="button" className="bp-attach-item" onClick={() => fileInputRef.current?.click()}><FileIcon size={16} color="#0ea5e9" />{t("msg.attach.file")}</button>
            </div>
          )}
          <input ref={imgInputRef} type="file" accept="image/*" hidden onChange={(e) => pickFile(e.target.files?.[0])} />
          <input ref={pdfInputRef} type="file" accept="application/pdf,.pdf" hidden onChange={(e) => pickFile(e.target.files?.[0])} />
          <input ref={audioInputRef} type="file" accept="audio/*" hidden onChange={(e) => pickFile(e.target.files?.[0])} />
          <input ref={fileInputRef} type="file" hidden onChange={(e) => pickFile(e.target.files?.[0])} />

          <button type="button" className="bp-attach-btn" onClick={() => setMenuOpen((v) => !v)} data-testid="messages-attach-btn" aria-label={t("msg.attach_aria")}>
            <Paperclip size={16} />
          </button>
          <input className="bp-chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder={t("msg.placeholder")} data-testid="messages-input" />
          <button type="submit" className="bp-chat-send" disabled={sending || (!text.trim() && !pending)} data-testid="messages-send">
            <Send size={16} />
          </button>
        </form>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* CREDITS                                                                     */
/* -------------------------------------------------------------------------- */
const CREDIT_PRODUCTS_DEFS = [
  { id: "personnel", nameKey: "credits.p1.name", min: 1000, max: 50000, rate: [2, 3], duration: [1, 100], deposit: 250, garantieKey: "credits.garantie.none", descKey: "credits.p1.desc", color: "#2563eb" },
  { id: "immobilier", nameKey: "credits.p2.name", min: 10000, max: 1000000, rate: [2, 3], duration: [60, 360], deposit: 500, garantieKey: "credits.garantie.mortgage", descKey: "credits.p2.desc", color: "#0ea5e9" },
  { id: "auto", nameKey: "credits.p3.name", min: 2000, max: 100000, rate: [2, 3], duration: [1, 120], deposit: 250, garantieKey: "credits.garantie.vehicle", descKey: "credits.p3.desc", color: "#f59e0b" },
  { id: "etudiant", nameKey: "credits.p4.name", min: 1000, max: 50000, rate: [2, 3], duration: [1, 120], deposit: 250, garantieKey: "credits.garantie.parental", descKey: "credits.p4.desc", color: "#8b5cf6" },
  { id: "professionnel", nameKey: "credits.p5.name", min: 5000, max: 1000000, rate: [2, 3], duration: [1, 240], deposit: 500, garantieKey: "credits.garantie.professional", descKey: "credits.p5.desc", color: "#16a34a" },
  { id: "express", nameKey: "credits.p6.name", min: 100, max: 5000, rate: [3, 4], duration: [1, 12], deposit: 100, garantieKey: "credits.garantie.none", descKey: "credits.p6.desc", color: "#ec4899" },
];

function calcMonthly(amount, durationMonths, annualRatePct) {
  const P = Number(amount);
  const n = Number(durationMonths);
  const r = annualRatePct / 100 / 12;
  if (!P || !n || n <= 0) return null;
  if (r === 0) return P / n;
  return (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

const normCreditProduct = (p, t) => ({
  id: p.id,
  name: p.name,
  description: p.description || "",
  color: p.color || "#2563eb",
  min: Number(p.amount_min ?? p.min ?? 0),
  max: Number(p.amount_max ?? p.max ?? 0),
  rate: [Number(p.rate_min ?? (p.rate?.[0]) ?? 2), Number(p.rate_max ?? (p.rate?.[1]) ?? 4)],
  duration: [Number(p.duration_min ?? (p.duration?.[0]) ?? 1), Number(p.duration_max ?? (p.duration?.[1]) ?? 120)],
  deposit: Number(p.deposit ?? 0),
  // Normalise garantie: backend may send legacy French strings, an i18n key, or nothing.
  // We always end up with a translated string in the active locale.
  garantie: (() => {
    const raw = p.garantie || "";
    if (!raw) return t("credits.garantie.none");
    // Treat known legacy French defaults as the "none" sentinel
    if (raw === "Aucune") return t("credits.garantie.none");
    if (raw === "Hypothèque sur le bien financé") return t("credits.garantie.mortgage");
    if (raw === "Véhicule financé") return t("credits.garantie.vehicle");
    if (raw === "Parentale ou bancaire") return t("credits.garantie.parental");
    if (raw === "Actifs professionnels ou caution personnelle") return t("credits.garantie.professional");
    // i18n key format
    if (raw.startsWith("credits.garantie.")) return t(raw);
    // Custom admin string — display as-is
    return raw;
  })(),
  // Marker used by the UI to hide the “garantie” detail line when there is none.
  hasGarantie: !!(p.garantie && p.garantie !== "Aucune" && p.garantie !== "credits.garantie.none"),
  eligibility: p.eligibility || "",
});

export function CreditsPage({ bank, style, headers, me, setActiveSection }) {
  const { t } = useContext(BankLangCtx);
  const [products, setProducts] = useState([]);
  const CREDIT_PRODUCTS = products;
  const [history, setHistory] = useState([]);
  const [detail, setDetail] = useState(null);
  const [requesting, setRequesting] = useState(null);
  const [form, setForm] = useState({ amount: "", duration: "", purpose: "" });

  const load = async () => {
    try { const { data } = await api.get("/bank-user/credits", { headers }); setHistory(data); }
    catch { setHistory([]); }
  };
  const loadProducts = async () => {
    try {
      const { data } = await api.get("/bank-user/credit-products", { headers });
      setProducts(data.map((p) => normCreditProduct(p, t)));
    } catch {
      setProducts(CREDIT_PRODUCTS_DEFS.map((p) => ({
        ...p,
        name: t(p.nameKey),
        description: t(p.descKey),
        garantie: t(p.garantieKey),
        hasGarantie: p.garantieKey !== "credits.garantie.none",
      })));
    }
  };
  useEffect(() => { if (headers) { load(); loadProducts(); } }, [headers]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/bank-user/credits", {
        product_id: requesting.id,
        product_name: requesting.name,
        amount: Number(form.amount),
        duration_months: Number(form.duration),
        initial_deposit: requesting.deposit,
        purpose: form.purpose,
      }, { headers });
      toast.success(t("credits.toast.submitted"));
      setRequesting(null); setForm({ amount: "", duration: "", purpose: "" });
      load();
    } catch (err) { toast.error(tError(err, t)); }
  };

  return (
    <>
      <style>{PAGES_CSS}</style>
      <p style={{ color: "var(--muted)", fontSize: ".85rem", margin: "0 0 1.25rem" }}>{t("credits.cta_sub")}</p>

      <div className="bp-products-grid">
        {CREDIT_PRODUCTS.map((p) => (
          <div key={p.id} className="bp-product">
            <div className="bp-product-head">
              <div className="bp-product-icon" style={{ background: p.color + "1F" }}>
                <FileText size={20} color={p.color} />
              </div>
              <h4 className="bp-product-name">{p.name}</h4>
            </div>
            <p className="bp-product-body">{p.description}</p>
            <div className="bp-product-meta">
              <span className="bp-meta-chip">{fmtMoney(p.min, me?.currency || bank?.default_currency)} – {fmtMoney(p.max, me?.currency || bank?.default_currency)}</span>
              <span className="bp-meta-chip">{p.rate[0]}% – {p.rate[1]}%</span>
              <span className="bp-meta-chip">{t("credits.duration_chip", { min: p.duration[0], max: p.duration[1] })}</span>
            </div>
            <div className="bp-product-actions">
              <button className="bp-btn ghost" onClick={() => setDetail(p)} data-testid={`credit-info-${p.id}`}>{t("credits.learn_more")}</button>
              <button className="bp-btn primary" onClick={() => { setRequesting(p); setForm({ amount: "", duration: "", purpose: "" }); }} data-testid={`credit-request-${p.id}`}>{t("credits.request")}</button>
            </div>
          </div>
        ))}
      </div>

      {history.length > 0 && (
        <>
          <h3 style={{ fontFamily: "'Syne', sans-serif", fontSize: ".95rem", fontWeight: 700, color: "var(--navy)", margin: "1.5rem 0 14px" }}>{t("credits.history")}</h3>
          <div>
            {history.map((h) => (
              <div key={h.id} className="bp-req">
                <div className="bp-product-icon" style={{ background: style.accent + "18", width: 36, height: 36 }}>
                  <FileText size={16} color={style.accent} />
                </div>
                <div className="bp-req-info">
                  <div className="bp-req-name">{h.product_name}</div>
                  <div className="bp-req-sub">{t("credits.n_months", { n: h.duration_months })} · {new Date(h.created_at).toLocaleDateString()}</div>
                </div>
                <div className="bp-req-amount">{fmtMoney(h.amount, me?.currency || bank?.default_currency)}</div>
                <span className={`bp-status ${h.status}`}>{h.status === "pending" ? t("credits.status.pending") : h.status === "approved" ? t("credits.status.accepted") : t("credits.status.refused")}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Detail modal */}
      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail?.name || ""} footer={(
        <>
          <button className="bp-btn ghost" onClick={() => setDetail(null)}>{t("credits.form.close")}</button>
          <button className="bp-btn primary" onClick={() => { setRequesting(detail); setDetail(null); }}>{t("credits.request")}</button>
        </>
      )}>
        {detail && (
          <>
            <p style={{ fontSize: ".88rem", color: "var(--navy)", lineHeight: 1.5, margin: "0 0 14px" }}>{detail.description}</p>
            <h4 style={{ fontFamily: "'Syne', sans-serif", fontSize: ".88rem", fontWeight: 700, color: "var(--navy)", margin: "0 0 10px" }}>{t("credits.chars")}</h4>
            <ul style={{ paddingLeft: 18, margin: "0 0 16px", fontSize: ".82rem", lineHeight: 1.8, color: "var(--navy)" }}>
              <li>{t("credits.amount_range", { min: detail.min.toLocaleString(), max: detail.max.toLocaleString() })}</li>
              <li>{t("credits.rate_range", { min: detail.rate[0], max: detail.rate[1] })}</li>
              <li>{t("credits.duration_range", { min: detail.duration[0], max: detail.duration[1] })}</li>
              <li>{t("credits.deposit_min", { amount: detail.deposit })}</li>
              {detail.hasGarantie && <li>{t("credits.garantie", { garantie: detail.garantie })}</li>}
            </ul>
            <h4 style={{ fontFamily: "'Syne', sans-serif", fontSize: ".88rem", fontWeight: 700, color: "var(--navy)", margin: "0 0 10px" }}>{t("credits.eligibility")}</h4>
            <ul style={{ paddingLeft: 18, margin: 0, fontSize: ".82rem", lineHeight: 1.8, color: "var(--navy)" }}>
              <li>{t("credits.elig.deposit", { amount: detail.deposit })}</li>
              <li>{detail.eligibility || t("credits.elig.income")}</li>
              {detail.garantie !== "Aucune" && <li>{t("credits.elig.garantie", { garantie: detail.garantie })}</li>}
            </ul>
          </>
        )}
      </Modal>

      {/* Request modal */}
      <Modal open={!!requesting} onClose={() => setRequesting(null)} title={requesting ? `${t("credits.request")} — ${requesting.name}` : ""} footer={(
        <>
          <button className="bp-btn ghost" onClick={() => setRequesting(null)}>{t("credits.form.cancel")}</button>
          <button className="bp-btn primary" onClick={submit}>{t("credits.form.submit")}</button>
        </>
      )}>
        {requesting && (() => {
          const midRate = (requesting.rate[0] + requesting.rate[1]) / 2;
          const monthly = calcMonthly(form.amount, form.duration, midRate);
          const totalCost = monthly ? monthly * Number(form.duration) : null;
          const curr = me?.currency || bank?.default_currency || "EUR";
          return (
            <form onSubmit={submit}>
              <div className="bp-field">
                <label className="bp-label">{t("credits.form.amount")}</label>
                <input className="bp-input" type="number" required min={requesting.min} max={requesting.max} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder={`${requesting.min} – ${requesting.max}`} />
              </div>
              <div className="bp-field">
                <label className="bp-label">{t("credits.form.duration")}</label>
                <input className="bp-input" type="number" required min={requesting.duration[0]} max={requesting.duration[1]} value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder={`${requesting.duration[0]} – ${requesting.duration[1]}`} />
              </div>
              {monthly && (
                <div style={{ background: requesting.color + "10", border: `1.5px solid ${requesting.color}35`, borderRadius: 12, padding: "12px 14px", marginBottom: 14 }}>
                  <div style={{ fontSize: ".7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: requesting.color, marginBottom: 8 }}>{t("credits.simulation")}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div style={{ textAlign: "center", background: "var(--surface)", borderRadius: 10, padding: "10px 6px" }}>
                      <div style={{ fontSize: "1.1rem", fontWeight: 700, color: requesting.color, fontFamily: "'DM Sans', sans-serif", letterSpacing: "-.03em", fontVariantNumeric: "tabular-nums lining-nums" }}>{fmtMoney(monthly, curr)}</div>
                      <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: 3 }}>{t("credits.monthly_payment")}</div>
                    </div>
                    <div style={{ textAlign: "center", background: "var(--surface)", borderRadius: 10, padding: "10px 6px" }}>
                      <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--navy)", fontFamily: "'DM Sans', sans-serif", letterSpacing: "-.03em", fontVariantNumeric: "tabular-nums lining-nums" }}>{fmtMoney(totalCost, curr)}</div>
                      <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: 3 }}>{t("credits.total_cost")}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: 8, textAlign: "center" }}>{t("credits.rate_used")} {midRate.toFixed(1)}% {t("credits.rate_annual")}</div>
                </div>
              )}
              <div className="bp-field">
                <label className="bp-label">{t("credits.form.reason")}</label>
                <textarea className="bp-textarea" rows="3" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder={t("credits.form.reason_ph")} />
              </div>
              <div style={{ background: requesting.color + "12", border: `1px solid ${requesting.color}40`, padding: 10, borderRadius: 8, fontSize: ".75rem", color: "var(--navy)" }}>
                <Info size={12} style={{ verticalAlign: "middle", marginRight: 5 }} />
                {t("credits.deposit_min", { amount: requesting.deposit })}{requesting.hasGarantie ? ` — ${t("credits.garantie", { garantie: requesting.garantie })}` : ""}
              </div>
            </form>
          );
        })()}
      </Modal>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* FINANCING                                                                   */
/* -------------------------------------------------------------------------- */
const FINANCING_TYPES_DEFS = [
  { id: "remboursable", nameKey: "financing.reimbursable", color: "#0ea5e9", shortKey: "financing.reimbursable_desc" },
  { id: "non-remboursable", nameKey: "financing.non_reimbursable", color: "#10b981", shortKey: "financing.non_reimbursable_desc" },
];

export function FinancingPage({ bank, style, headers, setActiveSection, me }) {
  const { t } = useContext(BankLangCtx);
  const [products, setProducts] = useState([]);
  const FINANCING_TYPES = products;
  const [history, setHistory] = useState([]);
  const [requesting, setRequesting] = useState(null);
  const [form, setForm] = useState({ project_name: "", amount: "", description: "", sector: "" });

  const load = async () => {
    try { const { data } = await api.get("/bank-user/financings", { headers }); setHistory(data); }
    catch { setHistory([]); }
  };
  const loadProducts = async () => {
    try {
      const { data } = await api.get("/bank-user/financing-products", { headers });
      setProducts(data.map((p) => ({ id: p.id, name: p.name, short: p.description || "", type: p.type || "remboursable", color: p.color || "#0ea5e9" })));
    } catch {
      setProducts(FINANCING_TYPES_DEFS.map((ft) => ({ ...ft, name: t(ft.nameKey), short: t(ft.shortKey), type: ft.id })));
    }
  };
  useEffect(() => { if (headers) { load(); loadProducts(); } }, [headers]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/bank-user/financings", {
        type: requesting.type || requesting.id,
        product_id: requesting.id,
        product_name: requesting.name,
        project_name: form.project_name,
        amount: Number(form.amount),
        description: form.description,
        sector: form.sector,
      }, { headers });
      toast.success(t("financing.form.submit"));
      setRequesting(null); setForm({ project_name: "", amount: "", description: "", sector: "" });
      load();
    } catch (err) { toast.error(tError(err, t)); }
  };

  return (
    <>
      <style>{PAGES_CSS}</style>
      <p style={{ color: "var(--muted)", fontSize: ".85rem", margin: "0 0 1.25rem" }}>{t("financing.cta_sub")}</p>

      <div className="bp-products-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
        {FINANCING_TYPES.map((ft) => (
          <div key={ft.id} className="bp-product">
            <div className="bp-product-head">
              <div className="bp-product-icon" style={{ background: ft.color + "1F" }}>
                <HandCoins size={20} color={ft.color} />
              </div>
              <h4 className="bp-product-name">{ft.name}</h4>
            </div>
            <p className="bp-product-body">{ft.short}</p>
            <div className="bp-product-actions">
              <button className="bp-btn primary" onClick={() => { setRequesting(ft); setForm({ project_name: "", amount: "", description: "", sector: "" }); }} data-testid={`financing-${ft.id}`}>
                {t("financing.request")}
              </button>
            </div>
          </div>
        ))}
      </div>

      {history.length > 0 && (
        <>
          <h3 style={{ fontFamily: "'Syne', sans-serif", fontSize: ".95rem", fontWeight: 700, color: "var(--navy)", margin: "1.5rem 0 14px" }}>{t("financing.history")}</h3>
          <div>
            {history.map((h) => (
              <div key={h.id} className="bp-req">
                <div className="bp-product-icon" style={{ background: style.accent + "18", width: 36, height: 36 }}>
                  <HandCoins size={16} color={style.accent} />
                </div>
                <div className="bp-req-info">
                  <div className="bp-req-name">{h.project_name}</div>
                  <div className="bp-req-sub">{h.type === "remboursable" ? t("financing.reimbursable_label") : t("financing.non_reimbursable_label")} · {new Date(h.created_at).toLocaleDateString()}</div>
                </div>
                <div className="bp-req-amount">{fmtMoney(h.amount, me?.currency || bank?.default_currency)}</div>
                <span className={`bp-status ${h.status}`}>{h.status === "pending" ? t("financing.status.pending") : h.status === "approved" ? t("financing.status.granted") : t("financing.status.refused")}</span>
              </div>
            ))}
          </div>
        </>
      )}

      <Modal open={!!requesting} onClose={() => setRequesting(null)} title={requesting ? t("financing.form.title", { name: requesting.name }) : ""} footer={(
        <>
          <button className="bp-btn ghost" onClick={() => setRequesting(null)}>{t("financing.form.cancel")}</button>
          <button className="bp-btn primary" onClick={submit}>{t("financing.form.submit")}</button>
        </>
      )}>
        {requesting && (
          <form onSubmit={submit}>
            <div className="bp-field">
              <label className="bp-label">{t("financing.form.project")}</label>
              <input className="bp-input" required value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} placeholder={t("financing.form.project_ph")} />
            </div>
            <div className="bp-row">
              <div className="bp-field">
                <label className="bp-label">{t("financing.form.amount")}</label>
                <input className="bp-input" type="number" min="1" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              </div>
              <div className="bp-field">
                <label className="bp-label">{t("financing.form.sector")}</label>
                <input className="bp-input" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} placeholder={t("financing.form.sector_ph")} />
              </div>
            </div>
            <div className="bp-field">
              <label className="bp-label">{t("financing.form.description")}</label>
              <textarea className="bp-textarea" rows="4" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t("financing.form.description_ph")} />
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* SETTINGS                                                                    */
/* -------------------------------------------------------------------------- */
export function SettingsPage({ headers }) {
  const { t } = useContext(BankLangCtx);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [loginHistory, setLoginHistory] = useState(null);

  useEffect(() => {
    if (!headers) return;
    api.get("/bank-user/login-history", { headers })
      .then(({ data }) => setLoginHistory(data))
      .catch(() => setLoginHistory([]));
  }, [headers]);

  const changePwd = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) {
      toast.error(t("settings.no_match"));
      return;
    }
    try {
      await api.post("/bank-user/change-password", { current_password: pwd.current_password, new_password: pwd.new_password }, { headers });
      toast.success(t("settings.changed"));
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(tError(err, t)); }
  };

  const fmtUA = (ua) => {
    if (!ua) return t("login.device.unknown");
    if (/mobile/i.test(ua)) return t("login.device.mobile");
    if (/tablet/i.test(ua)) return t("login.device.tablet");
    return t("login.device.desktop");
  };

  return (
    <>
      <style>{PAGES_CSS}</style>
      <form onSubmit={changePwd}>
        <div className="bp-settings-section">
          <h3 className="bp-settings-title"><Lock size={16} />{t("settings.security")}</h3>
          <div className="bp-field">
            <label className="bp-label">{t("settings.current_pw")}</label>
            <input className="bp-input" type="password" required value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} />
          </div>
          <div className="bp-row">
            <div className="bp-field">
              <label className="bp-label">{t("settings.new_pw")}</label>
              <input className="bp-input" type="password" minLength="6" required value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} />
            </div>
            <div className="bp-field">
              <label className="bp-label">{t("settings.confirm_pw")}</label>
              <input className="bp-input" type="password" minLength="6" required value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} />
            </div>
          </div>
        </div>
        <button type="submit" className="bp-btn primary"><ShieldCheck size={14} />{t("settings.submit")}</button>
      </form>

      <div className="bp-settings-section" style={{ marginTop: "1.5rem" }}>
        <h3 className="bp-settings-title"><ShieldCheck size={16} />{t("login.history")}</h3>
        {!loginHistory ? (
          <div style={{ color: "var(--muted)", fontSize: ".82rem" }}>…</div>
        ) : loginHistory.length === 0 ? (
          <div style={{ color: "var(--muted)", fontSize: ".82rem" }}>{t("login.history.empty")}</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {loginHistory.map((h, i) => (
              <div key={h.id || i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 12 }}>
                <div style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--accent)" + "18", display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <ShieldCheck size={16} color="var(--accent)" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: ".85rem", color: "var(--navy)" }}>
                    {new Date(h.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                    {i === 0 && <span style={{ marginLeft: 8, fontSize: ".68rem", background: "#dcfce7", color: "#15803d", padding: "1px 7px", borderRadius: 999, fontWeight: 700 }}>{t("login.recent")}</span>}
                  </div>
                  <div style={{ fontSize: ".72rem", color: "var(--muted)", marginTop: 2 }}>
                    {fmtUA(h.user_agent)} · IP {h.ip || "—"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
